import React, { useState, useMemo, useEffect } from 'react';
import { compose } from 'redux';
import { withProfile } from 'modules/v2/containers';
import { withAuth } from 'modules/auth/containers';
import DashboardTemplate from 'modules/v2/templates/DashboardTemplate';
import { DesignHuddleProvider, useDesignHuddle } from 'modules/v2/pages/TemplatesBuilder/DesignHuddleContext';
import RelativeLoading from 'modules/v2/common/AtomicDesign/RelativeLoading';
import { Divider } from 'antd';
import { Badge } from 'flowbite-react';
import { isEmpty, isEqual, map, uniqWith } from 'lodash';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { PreviewModal } from 'modules/v2/common/components';
import { useHistory } from 'react-router';

const getCategoryName = (categoryId) => {
  const categories = {
    1: 'Social Media',
    2: 'Presentations',
    3: 'Letter',
    4: 'Ads',
    7: 'Book Cover',
    9: 'Brochure',
    10: 'Infographic',
    11: 'Banner',
    12: 'Postcard',
    13: 'Business Card',
    14: 'AI Tool',
  };
  return categories[categoryId] || 'General';
};

const ProjectCard = ({ project, onClick, onCustomize, onPreview }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden max-w-[290px]">
    <div className="group hover:brightness-[90%] transition duration-300 ease-in-out aspect-[3/4] bg-gray-100 overflow-hidden relative max-h-[384px]">
      {project.thumbnail_url ? (
        <img
          src={project.thumbnail_url}
          alt={project.project_title || 'Project'}
          className="h-full w-full object-contain group-hover:scale-110 transition duration-300 ease-in-out"
          style={{ imageRendering: 'crisp-edges' }}
          onError={(e) => {
            const target = e.target;
            target.style.display = 'none';
            const nextElement = target.nextElementSibling;
            if (nextElement) {
              nextElement.style.display = 'flex';
            }
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400">
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
      )}
      {project.source_template?.primary_template_category_item_id && (
        <div className="absolute top-[5%] right-[5%]">
          <Badge className="text-orange-600 bg-orange-50 group-hover:bg-orange-50 text-sm leading-5 font-semibold">
            {getCategoryName(project.source_template.primary_template_category_item_id)}
          </Badge>
        </div>
      )}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="flex flex-col w-[90%] gap-2">
          <Button
            className="min-w-[100%]"
            onClick={(e) => {
              e.stopPropagation();
              onCustomize(project);
            }}
          >
            <span className="invisible group-hover:visible ">Customize</span>
          </Button>
          <Button
            className="min-w-[100%]"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(project);
            }}
            type="outlined"
          >
            <span className="flex gap-1 invisible group-hover:visible">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </span>
          </Button>
        </div>
      </div>
    </div>

    <div className="bg-white border-t border-gray-200 text-sm leading-5 font-semibold p-4 pt-2">
      {project?.project_title}
    </div>
  </div>
);

const PROJECT_TYPES = [
  { template_category_item_id: 0, item_name: 'All' },
  { template_category_item_id: 1, item_name: 'Social Media' },
  { template_category_item_id: 2, item_name: 'Presentations' },
  { template_category_item_id: 3, item_name: 'Letter' },
  { template_category_item_id: 4, item_name: 'Ads' },
  { template_category_item_id: 9, item_name: 'Brochure' },
  { template_category_item_id: 10, item_name: 'Infographic' },
  { template_category_item_id: 12, item_name: 'Postcard' },
  { template_category_item_id: 13, item_name: 'Business Card' },
  { template_category_item_id: 11, item_name: 'Banner' },
];

const MyProjectsContent = () => {
  const {
    legacyDhProjects,
    isLoadingProjects,
  } = useDesignHuddle();

  const history = useHistory();
  const [selectedType, setSelectedType] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewProject, setPreviewProject] = useState(null);

  const filteredProjects = useMemo(() => {
    if (!legacyDhProjects || legacyDhProjects.length === 0) return [];

    return legacyDhProjects.filter(project => {
      const matchesSearch = !searchQuery ||
        project.project_title?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === 0 ||
        project.source_template?.primary_template_category_item_id === selectedType;

      return matchesSearch && matchesType;
    });
  }, [legacyDhProjects, searchQuery, selectedType]);

  const handleProjectClick = (project) => {
    history.push(`/templates/project/${project.project_id}`);
  };

  const handlePreview = (project) => {
    if (project.thumbnail_url) {
      setPreviewProject(project);
      setShowPreviewModal(true);
    }
  };

  if (isLoadingProjects) {
    return <RelativeLoading isLoading={true} />;
  }

  return (
    <div className="w-full">
      {/* <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 transition-shadow overflow-hidden p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">My Projects</h2>
          <p className="text-gray-600 mb-6">
            Manage and edit your custom projects created from templates. Access your designs anytime and continue where you left off.
          </p>

          <div className="space-y-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Quick Access.</span> All your projects in one place for easy management.
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Continue Editing.</span> Pick up right where you left off with auto-saved progress.
              </div>
            </div>
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <div>
                <span className="font-semibold">Organized by Type.</span> Filter projects by category to find what you need faster.
              </div>
            </div>
          </div>
        </div>
      </div> */}

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {PROJECT_TYPES.map((category) => (
            <button
              key={category.template_category_item_id}
              onClick={() => setSelectedType(category.template_category_item_id)}
              className={`py-2 px-1 border-b-2 font-semibold text-sm whitespace-nowrap ${
                selectedType === category.template_category_item_id
                  ? 'border-cyan-500 text-neutral-800'
                  : 'border-transparent text-neutral-600 hover:text-neutral-700 hover:border-gray-300'
              }`}
            >
              {category.item_name}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white p-6 rounded-md shadow-md overflow-hidden">
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search projects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>

        <Divider />

        {filteredProjects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                onClick={handleProjectClick}
                onCustomize={handleProjectClick}
                onPreview={handlePreview}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">No projects found</div>
            <div className="text-gray-500 text-sm">
              {legacyDhProjects?.length === 0
                ? "You haven't created any projects yet. Start by customizing a template!"
                : "Try adjusting your search or filter criteria"
              }
            </div>
            {legacyDhProjects?.length === 0 && (
              <Button
                className="mt-4"
                onClick={() => history.push('/')}
              >
                Browse Templates
              </Button>
            )}
          </div>
        )}
      </div>

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={previewProject}
        onCustomize={handleProjectClick}
      />
    </div>
  );
};

const MyProjects = (props) => {
  return (
    <DesignHuddleProvider>
      <DashboardTemplate title="My Projects" centralized {...props}>
        <MyProjectsContent />
      </DashboardTemplate>
    </DesignHuddleProvider>
  );
};

export default compose(withProfile, withAuth)(MyProjects);
